/*!
 * Custom data action to add products to an existing sales order.
 */
import { createObservableDataAction, IAction, IActionContext, IActionInput, ICreateActionContext } from '@msdyn365-commerce/core';
import { pahcAddItemToExistingSalesOrderAsync } from './DataActionExtension.g';
import {
    IPahcAddProductToExistingSalesOrderResponseEntity,
    IPahcSalesLine,
    IPahcSalesOrderAddProductRequestEntity
} from './DataServiceEntities.g';

export interface IAddProductsToExistingOrderInput extends IActionInput {
    salesId: string;
    salesLines: IPahcSalesLine[];
}

export class AddProductsToExistingOrderInput implements IAddProductsToExistingOrderInput {
    public salesId: string;
    public salesLines: IPahcSalesLine[];

    public constructor(salesId: string, salesLines: IPahcSalesLine[]) {
        this.salesId = salesId;
        this.salesLines = salesLines;
    }

    public getCacheKey = () => `AddProductsToExistingOrder-${this.salesId}`;
    public getCacheObjectType = () => 'AddProductsToExistingOrder';
    public dataCacheType = () => 'none' as const;
}

const createInput = (inputData: ICreateActionContext): IAddProductsToExistingOrderInput => {
    const { requestContext } = inputData;
    // We expect caller to pass salesId and lines via config or request, but fall back to empty values.
    const salesId = (requestContext.query && (requestContext.query as any).salesId) || '';
    return new AddProductsToExistingOrderInput(salesId, []);
};

export async function addProductsToExistingOrderAction(
    input: IAddProductsToExistingOrderInput,
    ctx: IActionContext
): Promise<IPahcAddProductToExistingSalesOrderResponseEntity> {
    if (!input.salesId || !input.salesLines || input.salesLines.length === 0) {
        throw new Error('[addProductsToExistingOrderAction] salesId and salesLines are required');
    }

    const request: IPahcSalesOrderAddProductRequestEntity = {
        SalesId: input.salesId,
        SalesLines: input.salesLines
    };

    // Retail-proxy custom operation expects an `IContext` whose `callerContext`
    // contains a full `requestContext` (apiSettings, locale, etc.).
    const retailProxyContext: import('@msdyn365-commerce/retail-proxy').IContext = {
        callerContext: {
            requestContext: (ctx.requestContext as unknown) as import('@msdyn365-commerce/retail-proxy').ICallerContext['requestContext']
        }
    };

    const result = await pahcAddItemToExistingSalesOrderAsync(retailProxyContext, request);

    if (!result) {
        throw new Error('[addProductsToExistingOrderAction] Empty response from add-item API');
    }

    return result;
}

export default createObservableDataAction<IPahcAddProductToExistingSalesOrderResponseEntity>({
    id: 'add-products-to-existing-order',
    action: addProductsToExistingOrderAction as IAction<IPahcAddProductToExistingSalesOrderResponseEntity>,
    input: createInput
});

/**
 * Convenience helper for imperative usage from views/components.
 */
export const requestAddProductsToExistingOrder = (
    ctx: IActionContext,
    salesId: string,
    salesLines: IPahcSalesLine[]
): Promise<IPahcAddProductToExistingSalesOrderResponseEntity> =>
    addProductsToExistingOrderAction(new AddProductsToExistingOrderInput(salesId, salesLines), ctx);
